function debounce(fn, wait) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
    };
}
var dispatchCustomEvent = function dispatchCustomEvent(eventName) {
    var data =
        arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var detail = {
        detail: data,
    };
    var event = new CustomEvent(eventName, data ? detail : null);
    document.dispatchEvent(event);
};
window.recentlyViewedIds = [];

/**
 * Hide/show background page content from screen readers when a modal is open.
 * Targets the top-level Shopify section groups, the main content area and the
 * skip link, which lives outside #wrapper and would otherwise stay reachable
 * by Tab and by screen readers while a modal is open.
 */
var MODAL_BG_SELECTORS = [
    "#shopify-section-group-header-group",
    "#shopify-section-group-overlay-group",
    "#main-content",
    "#shopify-section-group-footer-group",
    ".screen-reader-shortcut",
];
function setPageInert(inert) {
    MODAL_BG_SELECTORS.forEach(function (sel) {
        var el = document.querySelector(sel);
        if (!el) return;
        if (inert) {
            el.setAttribute("inert", "");
        } else {
            el.removeAttribute("inert");
        }
    });
}

/**
 * Simple focus trap. Returns a cleanup function.
 */
function createFocusTrap(container) {
    var focusableSelectors =
        'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    function onKeydown(e) {
        if (e.key !== "Tab") return;
        var els = Array.from(
            container.querySelectorAll(focusableSelectors),
        ).filter(function (el) {
            if (el.closest("[inert]")) return false;
            var style = getComputedStyle(el);
            return style.display !== "none" && style.visibility !== "hidden";
        });
        if (!els.length) return;
        var first = els[0],
            last = els[els.length - 1];
        if (e.shiftKey) {
            if (document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
        } else {
            if (document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }
    container.addEventListener("keydown", onKeydown);
    return function () {
        container.removeEventListener("keydown", onKeydown);
    };
}

/**
 * The PhotoSwipe zoom dialog is rendered inside the product section, i.e.
 * inside #main-content, which setPageInert() marks inert while a modal is
 * open. Move it to <body> so inerting the page cannot inert the dialog itself,
 * and keep a single instance around when a section re-renders (theme editor)
 * or when two galleries exist on one page.
 */
function getZoomDialog() {
    var relocated = document.querySelector("body > .pswp");
    var all = Array.from(document.querySelectorAll(".pswp"));
    if (relocated) {
        all.forEach(function (el) {
            if (el !== relocated) el.remove();
        });
        return relocated;
    }
    var dialog = all[0];
    if (!dialog) return null;
    all.slice(1).forEach(function (el) {
        el.remove();
    });
    document.body.appendChild(dialog);
    return dialog;
}

/**
 * PhotoSwipe v4 only toggles aria-hidden on its own container; the page behind
 * it stays readable and tabbable. Wrap it in the same modal scaffolding the
 * other panels in this theme use. Call these from the "firstUpdate" and
 * "destroy" listeners of every PhotoSwipe instance.
 */
function zoomDialogOpened(dialog) {
    setPageInert(true);
    dialog._removeFocusTrap = createFocusTrap(dialog);
}
function zoomDialogClosed(dialog, focusTarget) {
    if (dialog._removeFocusTrap) {
        dialog._removeFocusTrap();
        dialog._removeFocusTrap = null;
    }
    setPageInert(false);
    if (focusTarget) {
        focusTarget.focus();
    }
}

/**
 *  @class
 *  @function Quantity
 */
if (!customElements.get("quantity-selector")) {
    class QuantityInput extends HTMLElement {
        constructor() {
            super();
            this.input = this.querySelector(".qty");
            this.step = this.input.getAttribute("step");
            this.changeEvent = new Event("change", {
                bubbles: true,
            });
            // Create buttons
            this.subtract = this.querySelector(".minus");
            this.add = this.querySelector(".plus");

            // Add functionality to buttons
            this.subtract.addEventListener("click", () =>
                this.change_quantity(-1 * this.step),
            );
            this.add.addEventListener("click", () =>
                this.change_quantity(1 * this.step),
            );
        }
        connectedCallback() {
            this.classList.add("buttons_added");
            this.validateQtyRules();
        }
        change_quantity(change) {
            // Get current value
            let quantity = Number(this.input.value);

            // Ensure quantity is a valid number
            if (isNaN(quantity)) quantity = 1;

            // Check for min & max
            if (this.input.getAttribute("min") > quantity + change) {
                return;
            }
            if (this.input.getAttribute("max")) {
                if (this.input.getAttribute("max") < quantity + change) {
                    return;
                }
            }
            // Change quantity
            quantity += change;

            // Ensure quantity is always a number
            quantity = Math.max(quantity, 1);

            // Output number
            this.input.value = quantity;

            this.input.dispatchEvent(this.changeEvent);

            this.validateQtyRules();

            // Announce quantity change to screen readers
            const output = this.querySelector("output");
            if (output) {
                const direction =
                    change > 0
                        ? output.dataset.i18nIncreased
                        : output.dataset.i18nDecreased;
                let msg = `${direction} ${quantity}.`;
                if (this.subtract.getAttribute("aria-disabled") === "true")
                    msg += ` ${output.dataset.i18nDecreaseDisabled}`;
                if (this.add.getAttribute("aria-disabled") === "true")
                    msg += ` ${output.dataset.i18nIncreaseDisabled}`;
                output.textContent = "";
                requestAnimationFrame(() => {
                    output.textContent = msg;
                });
            }
        }
        validateQtyRules() {
            const value = parseInt(this.input.value);
            if (this.input.min) {
                const isDisabled = value <= parseInt(this.input.min);
                this.subtract.classList.toggle("disabled", isDisabled);
                this.subtract.setAttribute("aria-disabled", String(isDisabled));
            }
            if (this.input.max) {
                const isDisabled = value >= parseInt(this.input.max);
                this.add.classList.toggle("disabled", isDisabled);
                this.add.setAttribute("aria-disabled", String(isDisabled));
            }
        }
    }
    customElements.define("quantity-selector", QuantityInput);
}

/**
 *  @class
 *  @function ArrowSubMenu
 */
class ArrowSubMenu {
    constructor(self) {
        this.submenu = self.parentNode.querySelector(".sub-menu");
        this.arrow = self;
        // Add functionality to buttons
        self.addEventListener("click", (e) => this.toggle_submenu(e));
    }

    toggle_submenu(e) {
        e.preventDefault();
        let submenu = this.submenu;

        if (!submenu.classList.contains("active")) {
            submenu.classList.add("active");
        } else {
            submenu.classList.remove("active");
            this.arrow.blur();
        }
    }
}
let arrows = document.querySelectorAll(".thb-arrow");
arrows.forEach((arrow) => {
    new ArrowSubMenu(arrow);
});

/**
 *  @class
 *  @function ProductCard
 */
if (!customElements.get("product-card")) {
    class ProductCard extends HTMLElement {
        constructor() {
            super();
            this.swatches = this.querySelector(".product-card-swatches");
            this.image = this.querySelector(
                ".product-featured-image-link .product-primary-image",
            );
            this.additional_images = this.querySelectorAll(
                ".product-secondary-image",
            );
            this.additional_images_nav = this.querySelectorAll(
                ".product-secondary-images-nav li",
            );
            this.quick_add = this.querySelector(
                ".product-card--add-to-cart-button-simple",
            );
            this.size_options = this.querySelector(".product-card-sizes");
        }
        connectedCallback() {
            if (this.swatches) {
                this.enableSwatches(this.swatches, this.image);
            }
            if (this.additional_images) {
                this.enableAdditionalImages();
            }
            if (this.quick_add) {
                this.enableQuickAdd();
            }
            if (this.size_options) {
                this.enableSizeOptions();
            }
        }
        enableAdditionalImages() {
            let image_length = this.additional_images.length;
            let images = this.additional_images;
            let nav = this.additional_images_nav;
            let image_container = this.querySelector(
                ".product-featured-image-link",
            );
            const mousemove = function (e) {
                let l = e.offsetX;
                let w = this.getBoundingClientRect().width;
                let prc = l / w;
                let sel = Math.floor(prc * image_length);
                let selimg = images[sel];
                images.forEach((image, index) => {
                    if (image.classList.contains("hover")) {
                        image.classList.remove("hover");
                        if (nav.length) {
                            nav[index].classList.remove("active");
                        }
                    }
                });
                if (selimg) {
                    if (!selimg.classList.contains("hover")) {
                        selimg.classList.add("hover");
                        if (nav.length) {
                            nav[sel].classList.add("active");
                        }
                    }
                }
            };
            const mouseleave = function (e) {
                images.forEach((image, index) => {
                    image.classList.remove("hover");
                    if (nav.length) {
                        nav[index].classList.remove("active");
                    }
                });
            };
            if (image_container) {
                image_container.addEventListener("touchstart", mousemove, {
                    passive: true,
                });
                image_container.addEventListener("touchmove", mousemove, {
                    passive: true,
                });
                image_container.addEventListener("touchend", mouseleave, {
                    passive: true,
                });
                image_container.addEventListener("mouseenter", mousemove, {
                    passive: true,
                });
                image_container.addEventListener("mousemove", mousemove, {
                    passive: true,
                });
                image_container.addEventListener("mouseleave", mouseleave, {
                    passive: true,
                });
            }

            images.forEach(function (image) {
                window.addEventListener("load", (event) => {
                    lazySizes.loader.unveil(image);
                });
            });
        }
        enableSwatches(swatches, image) {
            let swatch_list = swatches.querySelectorAll(".product-card-swatch"),
                org_srcset = image ? image.dataset.srcset : "";
            this.color_index = this.swatches.dataset.index;

            swatch_list.forEach((swatch, index) => {
                window.addEventListener("load", (event) => {
                    let image = new Image();
                    image.srcset = swatch.dataset.srcset;
                    lazySizes.loader.unveil(image);
                });

                swatch.addEventListener("mouseover", () => {
                    [].forEach.call(swatch_list, function (el) {
                        el.classList.remove("active");
                    });
                    if (image) {
                        if (swatch.dataset.srcset) {
                            image.setAttribute("srcset", swatch.dataset.srcset);
                        } else {
                            image.setAttribute("srcset", org_srcset);
                        }
                    }
                    if (this.size_options) {
                        this.current_options[this.color_index] =
                            swatch.querySelector("span").innerText;
                        this.updateMasterId();
                    }
                    swatch.classList.add("active");
                });
                swatch.addEventListener("click", function (evt) {
                    window.location.href = this.dataset.href;
                    evt.preventDefault();
                });
            });
        }
        enableQuickAdd() {
            this.quick_add.addEventListener("click", this.quickAdd.bind(this));
        }
        enableSizeOptions() {
            let size_list = this.size_options.querySelectorAll(
                    ".product-card-sizes--size",
                ),
                featured_image = this.querySelector(".product-featured-image"),
                has_hover = featured_image.classList.contains("thb-hover"),
                size_parent = this.size_options.parentElement;

            this.size_index = this.size_options.dataset.index;

            this.current_options = this.size_options.dataset.options.split(",");

            this.updateMasterId();

            size_parent.addEventListener(
                "mouseenter",
                () => {
                    if (has_hover) {
                        featured_image.classList.remove("thb-hover");
                    }
                },
                {
                    passive: true,
                },
            );
            size_parent.addEventListener(
                "mouseleave",
                () => {
                    if (has_hover) {
                        featured_image.classList.add("thb-hover");
                    }
                },
                {
                    passive: true,
                },
            );
            size_list.forEach((size) => {
                size.addEventListener("click", (evt) => {
                    evt.preventDefault();

                    if (size.classList.contains("is-disabled")) {
                        return;
                    }
                    this.current_options[this.size_index] =
                        size.querySelector("span").innerText;
                    this.updateMasterId();

                    size.classList.add("loading");
                    size.setAttribute("aria-disabled", true);
                    const config = {
                        method: "POST",
                        headers: {
                            "X-Requested-With": "XMLHttpRequest",
                            "Accept": "application/javascript",
                        },
                    };
                    let formData = new FormData();

                    formData.append("id", this.currentVariant.id);
                    formData.append("quantity", 1);
                    formData.append(
                        "sections",
                        this.getSectionsToRender().map(
                            (section) => section.section,
                        ),
                    );
                    formData.append("sections_url", window.location.pathname);

                    config.body = formData;

                    fetch(`${theme.routes.cart_add_url}`, config)
                        .then((response) => response.json())
                        .then((response) => {
                            if (response.status) {
                                return;
                            }
                            this.renderContents(response);

                            dispatchCustomEvent("cart:item-added", {
                                product: response.hasOwnProperty("items")
                                    ? response.items[0]
                                    : response,
                            });
                        })
                        .catch((e) => {
                            console.error(e);
                        })
                        .finally(() => {
                            size.classList.remove("loading");
                            size.removeAttribute("aria-disabled");
                        });
                });
            });
        }
        updateMasterId() {
            this.currentVariant = this.getVariantData().find((variant) => {
                return !variant.options
                    .map((option, index) => {
                        return this.current_options[index] === option;
                    })
                    .includes(false);
            });
            setTimeout(() => {
                this.setDisabled();
            }, 100);
        }
        getVariantData() {
            this.variantData =
                this.variantData ||
                JSON.parse(
                    this.querySelector('[type="application/json"]').textContent,
                );
            return this.variantData;
        }
        setDisabled() {
            const variant_data = this.getVariantData();

            if (variant_data) {
                if (this.currentVariant) {
                    const selected_options = this.currentVariant.options.map(
                        (value, index) => {
                            return {
                                value,
                                index: `option${index + 1}`,
                            };
                        },
                    );

                    const available_options = this.createAvailableOptionsTree(
                        variant_data,
                        selected_options,
                    );

                    const fieldset_options =
                        Object.values(available_options)[this.size_index];
                    if (fieldset_options) {
                        if (
                            this.size_options.querySelectorAll(
                                ".product-card-sizes--size",
                            ).length
                        ) {
                            this.size_options
                                .querySelectorAll(".product-card-sizes--size")
                                .forEach((input, input_i) => {
                                    input.classList.toggle(
                                        "is-disabled",
                                        fieldset_options[input_i].isUnavailable,
                                    );
                                });
                        }
                    }
                } else {
                    if (
                        this.size_options.querySelectorAll(
                            ".product-card-sizes--size",
                        ).length
                    ) {
                        this.size_options
                            .querySelectorAll(".product-card-sizes--size")
                            .forEach((input, input_i) => {
                                input.classList.add("is-disabled");
                            });
                    }
                }
            }
            return true;
        }
        createAvailableOptionsTree(variant_data, selected_options) {
            // Reduce variant array into option availability tree
            return variant_data.reduce(
                (options, variant) => {
                    // Check each option group (e.g. option1, option2, option3) of the variant
                    Object.keys(options).forEach((index) => {
                        if (variant[index] === null) return;

                        let entry = options[index].find(
                            (option) => option.value === variant[index],
                        );

                        if (typeof entry === "undefined") {
                            // If option has yet to be added to the options tree, add it
                            entry = {
                                value: variant[index],
                                isUnavailable: true,
                            };
                            options[index].push(entry);
                        }

                        // Check how many selected option values match a variant
                        const countVariantOptionsThatMatchCurrent =
                            selected_options.reduce(
                                (count, { value, index }) => {
                                    return variant[index] === value
                                        ? count + 1
                                        : count;
                                },
                                0,
                            );

                        // Only enable an option if an available variant matches all but one current selected value
                        if (
                            countVariantOptionsThatMatchCurrent >=
                            selected_options.length - 1
                        ) {
                            entry.isUnavailable =
                                entry.isUnavailable && variant.available
                                    ? false
                                    : entry.isUnavailable;
                        }

                        // Make sure if a variant is unavailable, disable currently selected option
                        if (
                            (!this.currentVariant ||
                                !this.currentVariant.available) &&
                            selected_options.find(
                                (option) =>
                                    option.value === entry.value &&
                                    index === option.index,
                            )
                        ) {
                            entry.isUnavailable = true;
                        }

                        // First option is always enabled
                        if (index === "option1") {
                            entry.isUnavailable =
                                entry.isUnavailable && variant.available
                                    ? false
                                    : entry.isUnavailable;
                        }
                    });

                    return options;
                },
                {
                    option1: [],
                    option2: [],
                    option3: [],
                },
            );
        }
        quickAdd(evt) {
            evt.preventDefault();
            if (this.quick_add.disabled) {
                return;
            }
            this.quick_add.classList.add("loading");
            this.quick_add.setAttribute("aria-disabled", true);

            const config = {
                method: "POST",
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    "Accept": "application/javascript",
                },
            };

            let formData = new FormData();

            formData.append("id", this.quick_add.dataset.productId);
            formData.append("quantity", 1);
            formData.append(
                "sections",
                this.getSectionsToRender().map((section) => section.section),
            );
            formData.append("sections_url", window.location.pathname);

            config.body = formData;

            fetch(`${theme.routes.cart_add_url}`, config)
                .then((response) => response.json())
                .then((response) => {
                    if (response.status) {
                        return;
                    }
                    this.renderContents(response);

                    dispatchCustomEvent("cart:item-added", {
                        product: response.hasOwnProperty("items")
                            ? response.items[0]
                            : response,
                    });
                })
                .catch((e) => {
                    console.error(e);
                })
                .finally(() => {
                    this.quick_add.classList.remove("loading");
                    this.quick_add.removeAttribute("aria-disabled");
                });

            return false;
        }
        getSectionsToRender() {
            return [
                {
                    id: "Cart",
                    section: "main-cart",
                    selector: ".thb-cart-form",
                },
                {
                    id: "Cart-Drawer",
                    section: "cart-drawer",
                    selector: ".cart-drawer",
                },
                {
                    id: "cart-drawer-toggle",
                    section: "cart-bubble",
                    selector: ".thb-item-count",
                },
            ];
        }
        renderContents(parsedState) {
            this.getSectionsToRender().forEach((section) => {
                if (!document.getElementById(section.id)) {
                    return;
                }
                const elementToReplace =
                    document
                        .getElementById(section.id)
                        .querySelector(section.selector) ||
                    document.getElementById(section.id);
                elementToReplace.innerHTML = this.getSectionInnerHTML(
                    parsedState.sections[section.section],
                    section.selector,
                );

                if (section.id === "Cart-Drawer") {
                    document.getElementById("Cart-Drawer")?.notesToggle();
                    document
                        .getElementById("Cart-Drawer")
                        ?.removeProductEvent();
                }

                if (section.id === "Cart" && typeof Cart !== "undefined") {
                    new Cart().renderContents(parsedState);
                }
            });

            if (document.getElementById("Cart-Drawer")) {
                document.getElementById("Cart-Drawer").classList.add("active");
                document.body.classList.add("open-cart");
                document.body.classList.add("open-cc");
                if (
                    document
                        .getElementById("Cart-Drawer")
                        .querySelector(".product-recommendations--full")
                ) {
                    document
                        .getElementById("Cart-Drawer")
                        .querySelector(".product-recommendations--full")
                        .classList.add("active");
                }
                dispatchCustomEvent("cart-drawer:open");
            }
        }
        getSectionInnerHTML(html, selector = ".shopify-section") {
            return new DOMParser()
                .parseFromString(html, "text/html")
                .querySelector(selector).innerHTML;
        }
    }
    customElements.define("product-card", ProductCard);
}

/**
 *  @class
 *  @function PanelClose
 */
if (!customElements.get("side-panel-close")) {
    class PanelClose extends HTMLElement {
        constructor() {
            super();
        }
        connectedCallback() {
            this.cc = document.querySelector(".click-capture");

            this.onClick = (e) => {
                let panel = document.querySelectorAll(".side-panel.active");
                if (panel.length) {
                    this.close_panel(e, panel[0]);
                }
            };
            // Add functionality to buttons
            this.addEventListener("click", this.onClick.bind(this));
            document.addEventListener("panel:close", this.onClick.bind(this));
            if (!this.cc.hasAttribute("initialized")) {
                this.cc.addEventListener("click", this.onClick.bind(this));
                this.cc.setAttribute("initialized", "");
            }
        }
        close_panel(e, panel) {
            if (e) {
                e.preventDefault();
            }
            if (!panel) {
                panel = e?.target.closest(".side-panel.active");

                if (!panel) {
                    return;
                }
            }
            if (
                panel.classList.contains("product-drawer") ||
                document.body.classList.contains("open-quick-view")
            ) {
                this.close_quick_view();
            } else if (panel.classList.contains("cart-drawer")) {
                if (panel.querySelector(".product-recommendations--full")) {
                    if (!document.body.classList.contains("open-quick-view")) {
                        panel
                            .querySelector(".product-recommendations--full")
                            .classList.remove("active");
                    }
                }
                if (window.innerWidth < 1069) {
                    if (!document.body.classList.contains("open-quick-view")) {
                        panel.classList.remove("active");
                        panel.setAttribute("inert", "");
                        document.body.classList.remove("open-cc");
                        document.body.classList.remove("open-cart");
                    } else {
                        this.close_quick_view();
                    }
                } else {
                    if (panel.querySelector(".product-recommendations--full")) {
                        if (
                            !document.body.classList.contains("open-quick-view")
                        ) {
                            setTimeout(() => {
                                panel.classList.remove("active");
                                panel.setAttribute("inert", "");
                                document.body.classList.remove("open-cc");
                                document.body.classList.remove("open-cart");
                            }, 500);
                        } else {
                            this.close_quick_view();
                        }
                    } else {
                        panel.classList.remove("active");
                        panel.setAttribute("inert", "");
                        document.body.classList.remove("open-cc");
                        document.body.classList.remove("open-cart");
                    }
                }
            } else {
                panel.classList.remove("active");
                panel.setAttribute("inert", "");
                document.body.classList.remove("open-cc");
            }
        }
        close_quick_view() {
            let panel = document.getElementById("Product-Drawer");

            if (panel.querySelector(".product-quick-images--container")) {
                panel
                    .querySelector(".product-quick-images--container")
                    .classList.remove("active");
            }
            if (window.innerWidth < 1069) {
                panel.classList.remove("active");
                panel.setAttribute("inert", "");
                if (
                    !document.body.classList.contains("open-cart") ||
                    !document.body.classList.contains("open-quick-view")
                ) {
                    document.body.classList.remove("open-cc");
                }
                document.body.classList.remove("open-quick-view");
            } else {
                if (panel.querySelector(".product-quick-images--container")) {
                    setTimeout(() => {
                        panel.classList.remove("active");
                        panel.setAttribute("inert", "");
                        if (
                            !document.body.classList.contains("open-cart") ||
                            !document.body.classList.contains("open-quick-view")
                        ) {
                            document.body.classList.remove("open-cc");
                        }
                        document.body.classList.remove("open-quick-view");
                        panel.querySelector(
                            "#Product-Drawer-Content",
                        ).innerHTML = "";
                    }, 500);
                }
            }
        }
    }
    customElements.define("side-panel-close", PanelClose);

    document.addEventListener("keyup", (e) => {
        if (e.code) {
            if (e.code.toUpperCase() === "ESCAPE") {
                dispatchCustomEvent("panel:close");
            }
        }
    });
}
/**
 *  @class
 *  @function CartDrawer
 */
if (!customElements.get("cart-drawer")) {
    class CartDrawer extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            let button = document.getElementById("cart-drawer-toggle");

            // Rebuy intercepts the cart icon click and renders its own Smart
            // Cart UI; the native drawer never visibly opens. Keep the body
            // classes (used by CSS) and the open event (used by other code),
            // but skip the modal scaffolding (active class, focus, inert,
            // focus trap) since Rebuy owns the UI.
            button.addEventListener("click", (e) => {
                e.preventDefault();
                document.body.classList.add("open-cc");
                document.body.classList.add("open-cart");
                setTimeout(() => {
                    this.querySelector(
                        ".product-recommendations--full",
                    )?.classList.add("active");
                });
                dispatchCustomEvent("cart-drawer:open");
            });

            this.debouncedOnChange = debounce((event) => {
                this.onChange(event);
            }, 300);

            document.addEventListener("cart:refresh", (event) => {
                this.refresh();
            });

            this.addEventListener("change", this.debouncedOnChange.bind(this));

            this.notesToggle();
            this.removeProductEvent();
        }
        onChange(event) {
            if (event.target.classList.contains("qty")) {
                this.updateQuantity(
                    event.target.dataset.index,
                    event.target.value,
                );
            }
        }
        removeProductEvent() {
            let removes = this.querySelectorAll(".remove");

            removes.forEach((remove) => {
                remove.addEventListener("click", (event) => {
                    this.updateQuantity(event.target.dataset.index, "0");

                    event.preventDefault();
                });
            });
        }
        getSectionsToRender() {
            return [
                {
                    id: "Cart-Drawer",
                    section: "cart-drawer",
                    selector: ".cart-drawer",
                },
                {
                    id: "cart-drawer-toggle",
                    section: "cart-bubble",
                    selector: ".thb-item-count",
                },
            ];
        }
        getSectionInnerHTML(html, selector) {
            return new DOMParser()
                .parseFromString(html, "text/html")
                .querySelector(selector).innerHTML;
        }
        notesToggle() {
            let notes_toggle = document.getElementById("order-note-toggle");

            if (!notes_toggle) {
                return;
            }

            notes_toggle.addEventListener("click", (event) => {
                notes_toggle.nextElementSibling.classList.add("active");
            });
            notes_toggle.nextElementSibling
                .querySelectorAll(
                    ".button, .order-note-toggle__content-overlay",
                )
                .forEach((el) => {
                    el.addEventListener("click", (event) => {
                        notes_toggle.nextElementSibling.classList.remove(
                            "active",
                        );
                        this.saveNotes();
                    });
                });
        }
        saveNotes() {
            fetch(`${theme.routes.cart_update_url}.js`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": `application/json`,
                },
                body: JSON.stringify({
                    note: document.getElementById("mini-cart__notes").value,
                }),
            });
        }
        updateQuantity(line, quantity) {
            this.querySelector(`#CartDrawerItem-${line}`)?.classList.add(
                "thb-loading",
            );
            const body = JSON.stringify({
                line,
                quantity,
                sections: this.getSectionsToRender().map(
                    (section) => section.section,
                ),
                sections_url: window.location.pathname,
            });

            dispatchCustomEvent("line-item:change:start", {
                quantity: quantity,
            });
            this.querySelector(
                ".product-recommendations--full",
            )?.classList.remove("active");

            fetch(`${theme.routes.cart_change_url}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": `application/json`,
                },
                ...{
                    body,
                },
            })
                .then((response) => {
                    return response.text();
                })
                .then((state) => {
                    const parsedState = JSON.parse(state);

                    this.getSectionsToRender().forEach((section) => {
                        const elementToReplace =
                            document
                                .getElementById(section.id)
                                .querySelector(section.selector) ||
                            document.getElementById(section.id);

                        if (parsedState.sections) {
                            elementToReplace.innerHTML =
                                this.getSectionInnerHTML(
                                    parsedState.sections[section.section],
                                    section.selector,
                                );
                        }
                    });

                    this.removeProductEvent();
                    this.notesToggle();
                    dispatchCustomEvent("line-item:change:end", {
                        quantity: quantity,
                        cart: parsedState,
                    });

                    this.querySelector(
                        `#CartDrawerItem-${line}`,
                    )?.classList.remove("thb-loading");
                });
        }
        refresh() {
            this.querySelector(
                ".product-recommendations--full",
            )?.classList.remove("active");
            let sections = "cart-drawer,cart-bubble";
            fetch(`${window.location.pathname}?sections=${sections}`)
                .then((response) => {
                    return response.text();
                })
                .then((state) => {
                    const parsedState = JSON.parse(state);

                    this.getSectionsToRender().forEach((section) => {
                        const elementToReplace =
                            document
                                .getElementById(section.id)
                                .querySelector(section.selector) ||
                            document.getElementById(section.id);

                        elementToReplace.innerHTML = this.getSectionInnerHTML(
                            parsedState[section.section],
                            section.selector,
                        );
                    });

                    this.removeProductEvent();
                    this.notesToggle();
                });
        }
    }
    customElements.define("cart-drawer", CartDrawer);
}

/**
 *  @class
 *  @function SelectWidth
 */
class SelectWidth {
    constructor() {
        let _this = this;
        // resize on initial load
        window.addEventListener("load", () => {
            document
                .querySelectorAll(".resize-select")
                .forEach(_this.resizeSelect);
        });

        // delegated listener on change
        document.body.addEventListener("change", (e) => {
            if (
                e.target.matches(".resize-select") &&
                e.target.offsetParent !== null
            ) {
                _this.resizeSelect(e.target);
            }
        });
        window.addEventListener("resize.resize-select", function () {
            document
                .querySelectorAll(".resize-select")
                .forEach(_this.resizeSelect);
        });
    }

    resizeSelect(sel) {
        let tempOption = document.createElement("option");
        tempOption.textContent = sel.selectedOptions[0].textContent;

        let tempSelect = document.createElement("select"),
            offset = 13;
        tempSelect.style.visibility = "hidden";
        tempSelect.style.position = "fixed";
        tempSelect.appendChild(tempOption);
        if (
            sel.classList.contains("thb-language-code") ||
            sel.classList.contains("thb-currency-code") ||
            sel.classList.contains("facet-filters__sort")
        ) {
            offset = 2;
        }
        sel.after(tempSelect);
        if (tempSelect.clientWidth > 0) {
            sel.style.width = `${+tempSelect.clientWidth + offset}px`;
        }
        tempSelect.remove();
    }
}

if (typeof SelectWidth !== "undefined") {
    new SelectWidth();
}

/**
 *  @class
 *  @function FooterMenuToggle
 */
class FooterMenuToggle {
    constructor() {
        document
            .querySelectorAll(
                ".thb-widget-title.collapsible:not(.collapsible--desktop)",
            )
            .forEach((button) => {
                button.addEventListener("click", () => {
                    button.setAttribute(
                        "aria-expanded",
                        button.getAttribute("aria-expanded") !== "true"
                            ? "true"
                            : "false",
                    );
                });
            });
    }
}

/**
 *  @class
 *  @function QuickView
 */
if (!customElements.get("quick-view")) {
    class QuickView extends HTMLElement {
        constructor() {
            super();
        }
        connectedCallback() {
            this.drawer = document.getElementById("Product-Drawer");
            this.body = document.body;

            this.addEventListener("click", this.setupEventListener.bind(this));
        }
        setupEventListener(e) {
            e.preventDefault();
            let productHandle = this.dataset.productHandle,
                href = `${theme.routes.root_url}/products/${productHandle}?view=quick-view`;

            // remove double `/` in case shop might have /en or language in URL
            href = href.replace("//", "/");
            if (!href || !productHandle) {
                return;
            }
            if (this.classList.contains("loading")) {
                return;
            }
            this.classList.add("loading");
            fetch(href, {
                method: "GET",
            })
                .then((response) => {
                    this.classList.remove("loading");
                    return response.text();
                })
                .then((text) => {
                    const sectionInnerHTML = new DOMParser()
                        .parseFromString(text, "text/html")
                        .querySelector("#Product-Drawer-Content").innerHTML;

                    this.renderQuickview(sectionInnerHTML, href, productHandle);
                });
        }
        renderQuickview(sectionInnerHTML, href, productHandle) {
            if (sectionInnerHTML) {
                this.drawer.querySelector("#Product-Drawer-Content").innerHTML =
                    sectionInnerHTML;

                let js_files = this.drawer
                    .querySelector("#Product-Drawer-Content")
                    .querySelectorAll("script");

                if (js_files.length > 0) {
                    var head = document.getElementsByTagName("head")[0];
                    js_files.forEach((js_file, i) => {
                        let script = document.createElement("script");
                        script.src = js_file.src;
                        head.appendChild(script);
                    });
                }

                setTimeout(() => {
                    if (Shopify && Shopify.PaymentButton) {
                        Shopify.PaymentButton.init();
                    }
                    if (window.ProductModel) {
                        window.ProductModel.loadShopifyXR();
                    }
                }, 300);

                this.body.classList.add("open-cc");
                this.body.classList.add("open-quick-view");
                this.drawer.classList.add("active");
                this.drawer.removeAttribute("inert");
                this.drawer._opener =
                    document.activeElement !== document.body
                        ? document.activeElement
                        : null;
                setPageInert(true);
                this.drawer._removeFocusTrap = createFocusTrap(this.drawer);

                this.drawer.querySelector(".side-panel-close button").focus();

                setTimeout(() => {
                    this.drawer
                        .querySelector(".product-quick-images--container")
                        .classList.add("active");
                });
                dispatchCustomEvent("quick-view:open", {
                    productUrl: href,
                    productHandle: productHandle,
                });
                addIdToRecentlyViewed(productHandle);
            }
        }
    }
    customElements.define("quick-view", QuickView);
}

/**
 *  @class
 *  @function SidePanelContentTabs
 */
if (!customElements.get("side-panel-content-tabs")) {
    class SidePanelContentTabs extends HTMLElement {
        constructor() {
            super();
            this.buttons = this.querySelectorAll("button");
            this.panels = this.parentElement.querySelectorAll(
                ".side-panel-content--tab-panel",
            );
        }
        connectedCallback() {
            this.setupAria();
            this.setupButtonObservers();
        }
        disconnectedCallback() {}
        setupAria() {
            const uid = `side-panel-tabs-${++SidePanelContentTabs.count}`;
            this.setAttribute("role", "tablist");

            this.buttons.forEach((button, i) => {
                const panel = this.panels[i];
                button.id = `${uid}-tab-${i}`;
                button.setAttribute("role", "tab");
                if (panel) {
                    panel.id = `${uid}-panel-${i}`;
                    panel.setAttribute("role", "tabpanel");
                    panel.setAttribute("tabindex", "0");
                    panel.setAttribute("aria-labelledby", button.id);
                    button.setAttribute("aria-controls", panel.id);
                }
            });

            const activePanel = [...this.panels].findIndex((panel) =>
                panel.classList.contains("tab-active"),
            );
            this.toggleActiveClass(Math.max(activePanel, 0));
        }
        setupButtonObservers() {
            this.buttons.forEach((item, i) => {
                item.addEventListener("click", (e) => {
                    this.toggleActiveClass(i);
                });
                item.addEventListener("keydown", (e) => {
                    const last = this.buttons.length - 1;
                    let next;
                    switch (e.key) {
                        case "ArrowRight":
                            next = i === last ? 0 : i + 1;
                            break;
                        case "ArrowLeft":
                            next = i === 0 ? last : i - 1;
                            break;
                        case "Home":
                            next = 0;
                            break;
                        case "End":
                            next = last;
                            break;
                        default:
                            return;
                    }
                    e.preventDefault();
                    this.toggleActiveClass(next);
                    this.buttons[next].focus();
                });
            });
        }
        toggleActiveClass(i) {
            this.buttons.forEach((button, j) => {
                const selected = i === j;
                button.classList.toggle("tab-active", selected);
                button.setAttribute("aria-selected", selected);
                button.setAttribute("tabindex", selected ? "0" : "-1");
            });

            this.panels.forEach((panel, j) => {
                panel.classList.toggle("tab-active", i === j);
            });
        }
    }
    SidePanelContentTabs.count = 0;

    customElements.define("side-panel-content-tabs", SidePanelContentTabs);
}

/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get("collapsible-row")) {
    // https://css-tricks.com/how-to-animate-the-details-element/
    class CollapsibleRow extends HTMLElement {
        constructor() {
            super();

            this.details = this.querySelector("details");
            this.summary = this.querySelector("summary");
            this.content = this.querySelector(".collapsible__content");

            // Store the animation object (so we can cancel it if needed)
            this.animation = null;
            // Store if the element is closing
            this.isClosing = false;
            // Store if the element is expanding
            this.isExpanding = false;
        }
        connectedCallback() {
            this.setListeners();
        }
        setListeners() {
            this.querySelector("summary").addEventListener("click", (e) =>
                this.onClick(e),
            );
        }
        instantClose() {
            this.tl.timeScale(10).reverse();
        }
        animateClose() {
            this.tl.timeScale(3).reverse();
        }
        animateOpen() {
            this.tl.timeScale(1).play();
        }
        onClick(e) {
            // Stop default behaviour from the browser
            e.preventDefault();
            // Add an overflow on the <details> to avoid content overflowing
            this.details.style.overflow = "hidden";
            // Check if the element is being closed or is already closed
            if (this.isClosing || !this.details.open) {
                this.open();
                // Check if the element is being openned or is already open
            } else if (this.isExpanding || this.details.open) {
                this.shrink();
            }
        }
        shrink() {
            // Set the element as "being closed"
            this.isClosing = true;

            // Store the current height of the element
            const startHeight = `${this.details.offsetHeight}px`;
            // Calculate the height of the summary
            const endHeight = `${this.summary.offsetHeight}px`;

            // If there is already an animation running
            if (this.animation) {
                // Cancel the current animation
                this.animation.cancel();
            }

            // Start a WAAPI animation
            this.animation = this.details.animate(
                {
                    // Set the keyframes from the startHeight to endHeight
                    height: [startHeight, endHeight],
                },
                {
                    duration: 250,
                    easing: "ease",
                },
            );

            // When the animation is complete, call onAnimationFinish()
            this.animation.onfinish = () => this.onAnimationFinish(false);
            // If the animation is cancelled, isClosing variable is set to false
            this.animation.oncancel = () => (this.isClosing = false);
        }

        open() {
            // Apply a fixed height on the element
            this.details.style.height = `${this.details.offsetHeight}px`;
            // Force the [open] attribute on the details element
            this.details.open = true;
            // Wait for the next frame to call the expand function
            window.requestAnimationFrame(() => this.expand());
        }

        expand() {
            // Set the element as "being expanding"
            this.isExpanding = true;
            // Get the current fixed height of the element
            const startHeight = `${this.details.offsetHeight}px`;
            // Calculate the open height of the element (summary height + content height)
            const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

            // If there is already an animation running
            if (this.animation) {
                // Cancel the current animation
                this.animation.cancel();
            }

            // Start a WAAPI animation
            this.animation = this.details.animate(
                {
                    // Set the keyframes from the startHeight to endHeight
                    height: [startHeight, endHeight],
                },
                {
                    duration: 400,
                    easing: "ease-out",
                },
            );
            // When the animation is complete, call onAnimationFinish()
            this.animation.onfinish = () => this.onAnimationFinish(true);
            // If the animation is cancelled, isExpanding variable is set to false
            this.animation.oncancel = () => (this.isExpanding = false);
        }

        onAnimationFinish(open) {
            // Set the open attribute based on the parameter
            this.details.open = open;
            // Clear the stored animation
            this.animation = null;
            // Reset isClosing & isExpanding
            this.isClosing = false;
            this.isExpanding = false;
            // Remove the overflow hidden and the fixed height
            this.details.style.height = this.details.style.overflow = "";
        }
    }
    customElements.define("collapsible-row", CollapsibleRow);
}

/**
 *  @function addIdToRecentlyViewed
 */
function addIdToRecentlyViewed(handle) {
    if (!handle) {
        let product = document.querySelector(".thb-product-detail");

        if (product) {
            handle = product.dataset.handle;
        }
    }
    if (!handle) {
        return;
    }
    if (window.localStorage) {
        let recentIds = window.localStorage.getItem("recently-viewed");
        if (recentIds != "undefined" && recentIds != null) {
            window.recentlyViewedIds = JSON.parse(recentIds);
        }
    }
    // Remove current product if already in recently viewed array
    var i = window.recentlyViewedIds.indexOf(handle);

    if (i > -1) {
        window.recentlyViewedIds.splice(i, 1);
    }

    // Add id to array
    window.recentlyViewedIds.unshift(handle);

    if (window.localStorage) {
        window.localStorage.setItem(
            "recently-viewed",
            JSON.stringify(window.recentlyViewedIds),
        );
    }
}

document.addEventListener("DOMContentLoaded", () => {
    if (typeof FooterMenuToggle !== "undefined") {
        new FooterMenuToggle();
    }

    // The closed Rebuy Smart Cart stays in the DOM off-screen with
    // aria-hidden="true", so its controls remain keyboard focusable.
    // Keep it inert while closed so focus can't land on hidden elements.
    let rebuyCartOpen = false;
    const syncRebuyCartInert = () => {
        const rebuyCart = document.getElementById("rebuy-cart");
        if (rebuyCart) {
            rebuyCart.inert = !rebuyCartOpen;
        }
        return rebuyCart;
    };

    // Rebuy injects its cart after load — apply inert once it appears.
    if (!syncRebuyCartInert()) {
        const rebuyCartObserver = new MutationObserver(() => {
            if (syncRebuyCartInert()) {
                rebuyCartObserver.disconnect();
            }
        });
        rebuyCartObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    // Rebuy fires this event when its Smart Cart closes, bypassing PanelClose.
    document.addEventListener("rebuy:smartcart.hide", () => {
        document.body.classList.remove("open-cc");
        document.body.classList.remove("open-cart");
        const toggle = document.getElementById("cart-drawer-toggle");
        const rebuyCart = document.getElementById("rebuy-cart");
        const focusWasInCart =
            rebuyCart && rebuyCart.contains(document.activeElement);
        rebuyCartOpen = false;
        syncRebuyCartInert();
        if (toggle) {
            toggle.setAttribute("aria-expanded", "false");
            if (focusWasInCart) {
                toggle.focus();
            }
        }
    });

    document.addEventListener("rebuy:smartcart.show", () => {
        rebuyCartOpen = true;
        syncRebuyCartInert();
        const toggle = document.getElementById("cart-drawer-toggle");
        if (toggle) {
            toggle.setAttribute("aria-expanded", "true");
        }
    });

    // When open-cc is removed from body, all panels are closed — restore page inert state.
    new MutationObserver(() => {
        if (!document.body.classList.contains("open-cc")) {
            setPageInert(false);
        }
    }).observe(document.body, { attributes: true, attributeFilter: ["class"] });

    // Klaviyo popup open/close — hide background from screen readers.
    document.addEventListener("klaviyoForms", (e) => {
        if (e.detail && e.detail.type === "open") {
            setPageInert(true);
        } else if (
            e.detail &&
            (e.detail.type === "close" || e.detail.type === "submit")
        ) {
            setPageInert(false);
        }
    });

    // Safety net for search and product drawers in case any third-party code
    // closes them without going through PanelClose.
    document.querySelectorAll(".side-panel").forEach((panel) => {
        new MutationObserver(() => {
            if (!panel.classList.contains("active")) {
                panel.setAttribute("inert", "");
                if (panel._removeFocusTrap) {
                    panel._removeFocusTrap();
                    panel._removeFocusTrap = null;
                }
                if (panel._opener) {
                    panel._opener.focus({ preventScroll: true });
                    panel._opener = null;
                }
            }
        }).observe(panel, { attributes: true, attributeFilter: ["class"] });
    });
});
